import React, { ReactNode, useEffect } from 'react';

import { Pressable, Skeleton, Text } from '@rbilabs/universal-components';
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';

import { useGeolocation } from 'state/geolocation';
import { useReversedUIContext } from 'state/reversed-ui';
import { theme } from 'styles/configure-theme';

export const StyledCtaButton = Pressable.withConfig({
  display: 'flex',
  flexDirection: 'row',
  _web: {
    cursor: 'pointer',
  },
  fontSize: {
    base: 'sm',
    md: 'md',
  },
});

interface CtaButtonProps {
  text?: string;
  cta: () => void;
  icon?: ReactNode;
  enableAnimation?: boolean;
}

const CtaButton = ({ text, cta, icon, enableAnimation = false }: CtaButtonProps) => {
  const { reversedUI } = useReversedUIContext();
  const { isPermissionGranted } = useGeolocation();

  const shouldShowAnimation = text && enableAnimation && isPermissionGranted;
  const scaleValue = useSharedValue<number>(shouldShowAnimation ? 1.5 : 1);
  const paddingRightAnim = useSharedValue<number>(shouldShowAnimation ? 12 : 0);
  const paddingTopAnim = useSharedValue<number>(shouldShowAnimation ? 4 : 2);

  const animatedStyles = useAnimatedStyle(() => ({
    transform: [{ scale: scaleValue.value }],
    paddingRight: paddingRightAnim.value,
    paddingTop: paddingTopAnim.value,
  }));

  useEffect(() => {
    if (shouldShowAnimation) {
      scaleValue.value = withTiming(1, { duration: 500 });
      paddingRightAnim.value = withTiming(0, { duration: 500 });
      paddingTopAnim.value = withTiming(2, { duration: 500 });
    }
  }, [shouldShowAnimation, text]);

  const component =
    text !== '' || !isPermissionGranted ? (
      <Animated.View style={animatedStyles}>
        <Text
          color={reversedUI ? theme.token('text-reversed') : theme.token('text-default')}
          fontWeight={isPermissionGranted && enableAnimation ? 'bold' : undefined}
        >
          {text}
        </Text>
      </Animated.View>
    ) : (
      <Skeleton mt={1} rounded="full" h="$5" width={'90px'} />
    );

  return (
    <StyledCtaButton onPress={cta} accessibilityRole="button">
      {component}
      {icon}
    </StyledCtaButton>
  );
};

export default CtaButton;
